import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { LayoutDashboard, LogOut, Users, ShieldCheck, KeyRound, ScrollText, UserCheck, ClipboardCheck } from "lucide-react";

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const forced = !!user?.must_change_password;

  const nav = [
    { to: "/inductions", label: "Requests & Invites", icon: ClipboardCheck },
  ];
  if (user?.role === "admin") {
    nav.push({ to: "/audit", label: "Audit Log", icon: ScrollText });
    nav.push({ to: "/admin/users", label: "Users", icon: ShieldCheck });
    nav.push({ to: "/admin/approvals", label: "Approvals", icon: UserCheck });
  }

  return (
    <div className="min-h-screen bg-background print-hidden">
      <div className="overflow-hidden bg-red-600 py-1.5" data-testid="testing-banner">
        <div className="marquee text-sm font-semibold text-white">
          This system is currently in TESTING — please report any problems to kcockram@gfm-uk.com&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;This system is currently in TESTING — please report any problems to kcockram@gfm-uk.com&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;
        </div>
      </div>
      <header className="sticky top-0 z-30 border-b border-border bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-8">
            <button onClick={() => navigate("/")} data-testid="nav-home">
              <Logo />
            </button>
            <nav className="hidden gap-1 md:flex">
              <button data-testid="nav-contractors" onClick={() => navigate("/")}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  pathname === "/" ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                }`}>
                <Users className="h-4 w-4" /> Contractors
              </button>
              {nav.map((n) => {
                const Active = pathname === n.to;
                const Icon = n.icon;
                return (
                  <button
                    key={n.to}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => navigate(n.to)}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      Active ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                    }`}
                  >
                    <Icon className="h-4 w-4" /> {n.label}
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold leading-tight">{user?.name}</div>
              <div className="text-xs text-muted-foreground">
                {user?.email}{user?.role === "site_user" ? " · Site user" : user?.role === "admin" ? " · Admin" : ""}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPwOpen(true)} data-testid="change-password-btn" title="Change password">
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-6 py-8">{children}</main>
      <footer className="hidden border-t border-border py-4 text-center text-xs text-muted-foreground md:block" data-testid="app-footer">
        Built by Karl Cockram for GFM LTD
      </footer>
      <ChangePasswordDialog open={pwOpen || forced} onOpenChange={setPwOpen} forced={forced} />
    </div>
  );
}
